import { Radio } from "@base-ui/react/radio";
import { RadioGroup } from "@base-ui/react/radio-group";
import { STAR_COLOR, STAR_PATH } from "@listou/shared/constants";
import { useState } from "react";

/**
 * Five stars filled to a fraction: 4.3 fills four and a third. Drawn as one clipped layer
 * over an empty one, so any average renders exactly, not rounded to halves.
 */
export function Stars({
  value,
  size = "size-5",
  className = "",
}: {
  value: number;
  size?: string;
  className?: string;
}) {
  const fill = Math.max(0, Math.min(5, value)) * 20;
  return (
    <span
      className={`relative inline-flex shrink-0 ${className}`}
      role="img"
      aria-label={`${formatRating(value)} sur 5`}
    >
      <span className="flex text-ink/15">
        {[0, 1, 2, 3, 4].map((index) => (
          <svg key={index} viewBox="0 0 24 24" className={size} aria-hidden>
            <path d={STAR_PATH} fill="currentColor" />
          </svg>
        ))}
      </span>
      <span
        className="absolute inset-0 flex overflow-hidden"
        style={{ width: `${fill}%`, color: STAR_COLOR }}
      >
        {[0, 1, 2, 3, 4].map((index) => (
          <svg key={index} viewBox="0 0 24 24" className={`${size} shrink-0`} aria-hidden>
            <path d={STAR_PATH} fill="currentColor" />
          </svg>
        ))}
      </span>
    </span>
  );
}

export const formatRating = (value: number) =>
  new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(
    value,
  );

const LABELS = ["", "Décevant", "Moyen", "Bien", "Très bien", "Parfait"];

/**
 * Star picker as a radio group: arrow keys move between stars, and hovering previews the
 * rating before the click, which is what people expect from stars.
 */
export function StarInput({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (value: number) => void;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const shown = hover ?? value ?? 0;
  return (
    <div className="flex flex-wrap items-center gap-3">
      <RadioGroup
        aria-label="Votre note"
        value={value ? String(value) : null}
        onValueChange={(next) => onChange(Number(next))}
        onPointerLeave={() => setHover(null)}
        className="flex"
      >
        {[1, 2, 3, 4, 5].map((star) => (
          <Radio.Root
            key={star}
            value={String(star)}
            aria-label={`${star} étoile${star > 1 ? "s" : ""}, ${LABELS[star]}`}
            onPointerEnter={() => setHover(star)}
            onMouseEnter={() => setHover(star)}
            className="grid size-11 cursor-pointer place-items-center rounded-full outline-none focus-visible:ring-3 focus-visible:ring-accent"
          >
            <svg
              viewBox="0 0 24 24"
              aria-hidden
              className={`size-9 transition-transform ${star <= shown ? "scale-105" : "text-ink/15"}`}
              style={star <= shown ? { color: STAR_COLOR } : undefined}
            >
              <path d={STAR_PATH} fill="currentColor" />
            </svg>
          </Radio.Root>
        ))}
      </RadioGroup>
      <span className="min-w-20 font-bold text-muted" aria-hidden>
        {LABELS[shown]}
      </span>
    </div>
  );
}
